import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import MeasurementsModal from './MeasurementsModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import { Measurement } from "../../../../types/Measurement";
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));


const Measurements: React.FC = () => {
  const [measurementdata, setmeasurementdata] = useState<Measurement[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Measurement | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchBs = async () => {
    try {
      const data = await apiRequest<Measurement[]>({
        method: 'get',
        url: 'measurements',
      });
      setmeasurementdata(data);
    } catch (error) {
      showError('Failed to fetch Measurement ');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchBs();
  }, []);

  const handleEditClick = (record: Measurement) => {
    setIsEditMode(true);
    setInitialValues({
      measurement_id: record.measurement_id,
      measurement_ledger_id: record.measurement_ledger_id,
      measurement_height: record.measurement_height,
      measurement_status: record.measurement_status,
      measurement_weight: record.measurement_weight,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchBs();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (measurement_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-measurements/${measurement_id}`,
      });
      showSuccess('Measurement  deleted');
      fetchBs();
    } catch {
      showError('Failed to delete Measurement ');
    }
  };

  const handleStatusToggle = async (MeasurementID: number) => {
    setmeasurementdata((prev) =>
      prev.map((item) =>
        item.measurement_id === MeasurementID
          ? { ...item, measurement_status: item.measurement_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-measurements/${MeasurementID}`,
      });
      showSuccess('Measurement  status updated');
    } catch (error) {
      showError('Failed to update status');
      setmeasurementdata((prev) =>
        prev.map((item) =>
          item.measurement_id === MeasurementID
            ? { ...item, measurement_status: item.measurement_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Measurement>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Measurement> = [
    {
      title: 'Customer',
      dataIndex: 'ledger_id',
      key: 'ledger_id',
    },
    {
      title: 'Measurement type',
      dataIndex: 'measurement_height',
      key: 'measurement_height',
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.measurement_height || null,

      sortOrder: sortedInfo.columnKey === 'measurement_height' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.measurement_height}</div>,
    }
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Measurement) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.measurement_status}
                onChange={() => {
                  if (record.measurement_id !== undefined) {
                    handleStatusToggle(record.measurement_id);
                  } else {
                    console.error("Measurement ID is missing");
                  }
                }}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this bs?"
              onConfirm={() => {
                if (record.measurement_id !== undefined) {
                  handleDelete(record.measurement_id);
                } else {
                  console.error("Measurement ID is missing");
                }
              }}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Measurements </Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Measurement
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Measurement>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="measurement_id"
          dataSource={measurementdata.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No Measurement  found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, measurementdata.length)} of{' '}
                {measurementdata.length}
              </span>
              <Pagination
                {...pagination}
                total={measurementdata.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <MeasurementsModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        measurementdata={measurementdata}
      />
    </>
  );
};

export default Measurements;